'use client'

import type { Route } from 'next'
import Link from 'next/link'
import { ChevronDown, Menu, X } from 'lucide-react'
import * as React from 'react'
import { BrandLogo } from '@/components/brand/brand-logo'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { cn } from '@/lib/utils'
import { isNavGroup } from '@/lib/navigation'
import type { Action, Brand, NavGroup, NavItem, NavNode, NavigationContent } from '@/config'

type SiteHeaderProps = Readonly<{
  brand: Brand
  navigation: NavigationContent
}>

// ─── Shared link primitive ────────────────────────────────────────────────────

function NavLink({
  href,
  label,
  external,
  className,
  onClick,
  children,
}: {
  href: string
  label: string
  external?: boolean
  className?: string
  onClick?: () => void
  children?: React.ReactNode
}) {
  return (
    <Link
      href={href as Route}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      className={className}
      onClick={onClick}
    >
      {children ?? label}
    </Link>
  )
}

// ─── Desktop: dropdown group ──────────────────────────────────────────────────

function DesktopGroup({ group }: { group: NavGroup }) {
  const [open, setOpen] = React.useState(false)
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  function clearCloseTimer() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  function openMenu() {
    clearCloseTimer()
    setOpen(true)
  }

  function scheduleClose() {
    clearCloseTimer()
    closeTimer.current = setTimeout(() => {
      setOpen(false)
      closeTimer.current = null
    }, 120)
  }

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      clearCloseTimer()
    }
  }, [])

  return (
    <div
      className="group relative"
      onPointerEnter={openMenu}
      onPointerLeave={scheduleClose}
      onFocusCapture={openMenu}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          scheduleClose()
        }
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(p => !p)}
        className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        {group.label}
        <ChevronDown
          className={cn('h-3 w-3 transition-transform duration-200', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      <div
        role="region"
        onPointerEnter={openMenu}
        onPointerLeave={scheduleClose}
        className={cn(
          'invisible absolute left-0 top-full z-[9999] pt-2 opacity-0 transition-opacity duration-150',
          'pointer-events-none group-hover:visible group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:visible group-focus-within:pointer-events-auto group-focus-within:opacity-100',
          open && 'visible pointer-events-auto opacity-100',
        )}
      >
        <div className="flex items-start">
          <div className="w-[28rem] rounded-2xl border border-border bg-card p-2 text-card-foreground shadow-xl shadow-black/[0.06]">
            <div className="grid gap-1">
              {group.items.map(item => (
                <NavLink
                  key={item.href}
                  href={item.href}
                  label={item.label}
                  external={item.external}
                  onClick={() => setOpen(false)}
                  className="flex flex-col rounded-xl px-3 py-3 text-sm transition-colors hover:bg-accent"
                >
                  <span className="font-medium text-foreground">{item.label}</span>
                  {item.description && (
                    <span className="mt-0.5 text-xs leading-snug text-muted-foreground">
                      {item.description}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Desktop nav bar ──────────────────────────────────────────────────────────

function DesktopNav({ nodes }: { nodes: NavNode[] }) {
  return (
    <nav className="hidden items-center gap-1 xl:flex" aria-label="Primary navigation">
      {nodes.map(node =>
        isNavGroup(node) ? (
          <DesktopGroup key={node.label} group={node} />
        ) : (
          <NavLink
            key={(node as NavItem).href}
            href={(node as NavItem).href}
            label={node.label}
            external={(node as NavItem).external}
            className="inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          />
        ),
      )}
    </nav>
  )
}

// ─── Mobile: accordion group ──────────────────────────────────────────────────

function MobileGroup({ group, onNavigate }: { group: NavGroup; onNavigate: () => void }) {
  const [open, setOpen] = React.useState(false)

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(p => !p)}
        className="flex w-full items-center justify-between py-3 text-sm font-medium text-foreground"
      >
        {group.label}
        <ChevronDown
          className={cn('h-4 w-4 text-muted-foreground transition-transform duration-200', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="pb-2 pl-3">
          {group.items.map(item => (
            <NavLink
              key={item.href}
              href={item.href}
              label={item.label}
              external={item.external}
              onClick={onNavigate}
              className="flex flex-col py-2.5 text-sm"
            >
              <span className="font-medium text-foreground">{item.label}</span>
              {item.description && (
                <span className="mt-0.5 text-xs text-muted-foreground">{item.description}</span>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Mobile drawer + trigger ──────────────────────────────────────────────────

function MobileNav({ nodes, actions }: { nodes: NavNode[]; actions: Action[] }) {
  const [open, setOpen] = React.useState(false)

  function close() { setOpen(false) }

  React.useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      <button
        type="button"
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        aria-controls="mobile-nav-drawer"
        onClick={() => setOpen(p => !p)}
        className="inline-flex xl:hidden items-center justify-center rounded-lg border border-border bg-background p-2 text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        {open
          ? <X className="h-4 w-4" aria-hidden="true" />
          : <Menu className="h-4 w-4" aria-hidden="true" />}
      </button>

      {open && (
        <>
          <div
            aria-hidden="true"
            onClick={close}
            className="fixed inset-0 z-30 bg-background/60 backdrop-blur-sm xl:hidden"
          />

          <div
            id="mobile-nav-drawer"
            className="fixed inset-x-0 top-[var(--header-h,3.75rem)] z-40 max-h-[calc(100dvh-var(--header-h,3.75rem))] overflow-y-auto border-b border-border/70 bg-background shadow-lg xl:hidden"
          >
            <div className="mx-auto w-full max-w-[var(--container-page)] px-4 py-4 sm:px-6 lg:px-8">
              <div className="divide-y divide-border/50">
                {nodes.map(node =>
                  isNavGroup(node) ? (
                    <MobileGroup key={node.label} group={node} onNavigate={close} />
                  ) : (
                    <NavLink
                      key={(node as NavItem).href}
                      href={(node as NavItem).href}
                      label={node.label}
                      external={(node as NavItem).external}
                      onClick={close}
                      className="block py-3 text-sm font-medium text-foreground"
                    />
                  ),
                )}
              </div>

              <div className="mt-6 flex flex-col gap-3 border-t border-border/70 pt-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Theme</span>
                  <ThemeToggle compact />
                </div>
                {actions.map(action => (
                  <Button
                    key={action.href}
                    variant={action.variant === 'primary' ? 'default' : 'outline'}
                    asChild
                    className="w-full"
                  >
                    <NavLink
                      href={action.href}
                      label={action.label}
                      external={action.external}
                      onClick={close}
                    />
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

// ─── Site Header ──────────────────────────────────────────────────────────────

export function SiteHeader({ brand, navigation }: SiteHeaderProps) {
  const [scrolled, setScrolled] = React.useState(false)
  const headerRef = React.useRef<HTMLElement>(null)

  // Phase 4: shrink on scroll
  React.useEffect(() => {
    function onScroll() { setScrolled(window.scrollY > 60) }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Phase 4: expose header height as CSS var so mobile drawer tracks it
  React.useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height
      if (h) document.documentElement.style.setProperty('--header-h', `${h}px`)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const primaryAction = navigation.actions.find(a => a.variant === 'primary')
  const nodes = navigation.groups as NavNode[]

  return (
    <header
      ref={headerRef}
      className={cn(
        'sticky top-0 z-[70] isolate border-b border-border/70 bg-background/80 backdrop-blur-xl',
        'transition-[padding] duration-300',
        scrolled ? 'py-2' : 'py-3',
      )}
    >
      <div className="mx-auto flex w-full max-w-[var(--container-page)] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">

        {/* Logo — no tagline (Phase 1) */}
        <Link
          href="/"
          aria-label={`${brand.name} home`}
          className="inline-flex shrink-0 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <BrandLogo
            brand={brand}
            sizes="(min-width: 640px) 151px, 134px"
            className="h-8 w-[134px] sm:h-9 sm:w-[151px]"
          />
        </Link>

        {/* Desktop mega-menu (Phases 2 + 3) */}
        <DesktopNav nodes={nodes} />

        {/* Right: theme toggle + single CTA + mobile trigger */}
        <div className="flex shrink-0 items-center gap-2">
          <div className="hidden xl:flex">
            <ThemeToggle compact />
          </div>
          {primaryAction && (
            <Button asChild size="sm" className="hidden sm:inline-flex">
              <NavLink
                href={primaryAction.href}
                label={primaryAction.label}
                external={primaryAction.external}
              />
            </Button>
          )}
          <MobileNav nodes={nodes} actions={navigation.actions} />
        </div>

      </div>
    </header>
  )
}
